import { BaseObj } from "../models/UserdataSync";
import { AppConfig } from "../app.config";
import { ErrorHandlerService } from "./errorHandler.service";
import { Injectable } from "@angular/core";

export class DbConnection {
  public bancoUserdata!: IDBOpenDBRequest;
  public bancoLeis!: IDBOpenDBRequest;
  public bancoBuscas!: IDBOpenDBRequest;
  public bancoSvdm!: IDBOpenDBRequest;
}

@Injectable()
export class StorageService {
  constructor(private errorHandlerService: ErrorHandlerService) {}

  public checkForSupport(): boolean {
    const notSupported = !indexedDB;
    return !notSupported;
  }

  public connectDatabases(): DbConnection {
    const request = new DbConnection();

    request.bancoUserdata = indexedDB.open("userdata", AppConfig.versaoIndexDB);
    request.bancoBuscas = indexedDB.open("buscas", AppConfig.versaoIndexDB);
    request.bancoSvdm = indexedDB.open("svdm", AppConfig.versaoIndexDB);

    request.bancoBuscas.onupgradeneeded = (event) => {
      this.upgradeBancoBuscas(event);
    };

    request.bancoBuscas.onerror = (event) => {
      this.errorHandlerService.handleError(
        Error("Erro ao conectar à base IndexedDB" + JSON.stringify(event))
      );
    };

    request.bancoUserdata.onupgradeneeded = (event) => {
      this.upgradeBancoUserData(event);
    };

    request.bancoUserdata.onerror = (event) => {
      this.errorHandlerService.handleError(
        Error("Erro ao conectar à base IndexedDB" + JSON.stringify(event))
      );
    };

    request.bancoSvdm.onupgradeneeded = (event) => {
      this.upgradeBancoSvdmData(event);
    };

    request.bancoSvdm.onerror = (event) => {
      this.errorHandlerService.handleError(
        Error("Erro ao conectar à base IndexedDB" + JSON.stringify(event))
      );
    };

    return request;
  }

  public deletarDBs() {
    const bancosExcluir = ["buscas", "userdata", "svdm"];

    for (const banco of bancosExcluir) {
      this.excluirBanco(banco);
    }

    this.connectDatabases();
  }

  // public async clearDB(nomeBanco: string): Promise<void> {
  //   const request = new DbConnection();

  //   await this.excluirBanco(nomeBanco);

  //   request.bancoSvdm = indexedDB.open(nomeBanco, AppConfig.versaoIndexDB);
  // }

  public getByKey<T extends BaseObj>(
    id: string,
    banco: string,
    tabela: string
  ): Promise<T> {
    try {
      return new Promise((onsuccess, onerror) => {
        const connRequest = indexedDB.open(banco);

        connRequest.onsuccess = (event) => {
          try {
            const db = (<IDBOpenDBRequest>event.target).result;
            const store = db.transaction([tabela]).objectStore(tabela);
            const getRequest = store.get(id);

            getRequest.onsuccess = () => {
              onsuccess(<T>getRequest.result);
            };
            getRequest.onerror = () => {
              this.errorHandlerService.handleError(getRequest.error);
              onerror(getRequest?.error?.message);
            };
          } catch (error) {
            this.errorHandlerService.handleError(error);
            onerror(error);
          }
        };

        connRequest.onerror = (error) => {
          this.errorHandlerService.handleError(error);
          onerror();
        };
      });
    } catch (err: any) {
      throw new Error(
        `Erro em storage.helper.getByKey. Tabela: ${tabela}. Detalhes: ${err?.message}`
      );
    }
  }

  public list<T extends BaseObj>(
    banco: string,
    tabela: string,
    filter: (item: T) => boolean = () => true
  ): Promise<T[]> {
    try {
      const execute = (): Promise<T[]> => {
        const objs = new Array<T>();

        return new Promise((onsuccess, onerror) => {
          const connRequest = indexedDB.open(banco);

          connRequest.onsuccess = (event) => {
            // Um erro lançado aqui dentro (ex.: tabela inexistente) não
            // rejeita a promise sozinho; sem o try/catch ela nunca terminava
            try {
              const db = (<IDBOpenDBRequest>event.target).result;
              const store = db.transaction([tabela]).objectStore(tabela);
              const getRequest = store.openCursor();

              getRequest.onsuccess = (getEvent) => {
                try {
                  const cursor = (<IDBRequest>getEvent.target).result;
                  if (cursor) {
                    const e = <T>cursor.value;

                    if (!e.removido && (!filter || filter(e))) {
                      objs.push(e);
                    }

                    cursor.continue();
                  } else {
                    onsuccess(objs);
                  }
                } catch (error) {
                  onerror(error);
                }
              };

              getRequest.onerror = () => {
                this.errorHandlerService.handleError(getRequest.error);
                onerror(getRequest?.error?.message);
              };
            } catch (error) {
              onerror(error);
            }
          };

          connRequest.onerror = (error) => {
            this.errorHandlerService.handleError(error);
            onerror(error);
          };
        });
      };

      return new Promise((onsuccess, onerror) => {
        execute()
          .then((lista) => {
            onsuccess(lista);
          })
          .catch((error) => {
            this.errorHandlerService.handleError(error);
            // Sem rejeitar aqui a promise nunca terminava e quem estava
            // aguardando (ex.: carregamento da lei) ficava preso para sempre
            onerror(error);
          });
      });
    } catch (err: any) {
      throw new Error(
        `Erro em storage.helper.list. Tabela: ${tabela}. Detalhes: ${err?.message}`
      );
    }
  }

  public upsert<T extends BaseObj>(
    obj: T,
    banco: string,
    tabela: string,
    audit = true
  ): Promise<T> {
    try {
      return new Promise((onsuccess, onerror) => {
        const request = indexedDB.open(banco);

        request.onsuccess = (e) => {
          try {
            const db = (<IDBOpenDBRequest>e.target).result;
            const store = db
              .transaction([tabela], "readwrite")
              .objectStore(tabela);

            if (audit || !obj.dataHoraModificacao) {
              obj.dataHoraModificacao = new Date();
            }

            const requestStore = store.put(obj);

            requestStore.onsuccess = () => onsuccess(obj);
            requestStore.onerror = () => {
              this.errorHandlerService.handleError(requestStore.error);
              onerror(requestStore?.error?.message);
            };
          } catch (error) {
            this.errorHandlerService.handleError(error);
            onerror(error);
          }
        };

        request.onerror = (error) => {
          this.errorHandlerService.handleError(error);
          onerror(error);
        };
      });
    } catch (err: any) {
      throw new Error(
        `Erro em storage.helper.upsert. Tabela: ${tabela}. Detalhes: ${err?.message}`
      );
    }
  }

  public upsertMany<T extends BaseObj>(
    objs: T[],
    banco: string,
    tabela: string,
    audit = true
  ): Promise<T[]> {
    try {
      return new Promise((onsuccess, onerror) => {
        const savedObjects = new Array<T>();

        const tasks = objs.reduce(
          (accumulatorPromise, nextID) =>
            accumulatorPromise.then(() =>
              this.upsert(nextID, banco, tabela, audit).then((obj) => {
                savedObjects.push(obj);
              })
            ),
          Promise.resolve()
        );

        tasks
          .then(() => {
            onsuccess(savedObjects);
          })
          .catch((error) => {
            this.errorHandlerService.handleError(error);
            onerror(error);
          });
      });
    } catch (err: any) {
      throw new Error(
        `Erro em storage.helper.upsertMany. Tabela: ${tabela}. Detalhes: ${err?.message}`
      );
    }
  }

  public delete(banco: string, tabela: string, id: string): Promise<void> {
    try {
      return new Promise((onsuccess, onerror) => {
        const request = indexedDB.open(banco);

        request.onsuccess = (e) => {
          try {
            const db = (<IDBOpenDBRequest>e.target).result;
            const store = db
              .transaction([tabela], "readwrite")
              .objectStore(tabela);

            const requestDelete = store.delete(id);

            requestDelete.onsuccess = () => onsuccess();
            requestDelete.onerror = () => {
              this.errorHandlerService.handleError(requestDelete.error);
              onerror(requestDelete?.error?.message);
            };
          } catch (error) {
            this.errorHandlerService.handleError(error);
            onerror(error);
          }
        };

        request.onerror = (error) => {
          this.errorHandlerService.handleError(error);
          onerror(error);
        };
      });
    } catch (err: any) {
      throw new Error(
        `Erro em storage.helper.delete. Tabela: ${tabela}. Detalhes: ${err?.message}`
      );
    }
  }

  private tryDeleteObjectStore(db: IDBDatabase, tableName: string) {
    if (db.objectStoreNames.contains(tableName)) {
      db.deleteObjectStore(tableName);
    }
  }

  private upgradeBancoBuscas(event: IDBVersionChangeEvent) {
    const db = (<IDBOpenDBRequest>event.target).result;

    if (event.oldVersion > 0) {
      this.tryDeleteObjectStore(db, "buscas");
    }

    const objectStore = db.createObjectStore("buscas", { keyPath: "id" });
    objectStore.createIndex("id", "id", { unique: true });
    objectStore.createIndex("guia", "guiId", { unique: true });
  }

  private upgradeBancoUserData(event: IDBVersionChangeEvent) {
    const db = (<IDBOpenDBRequest>event.target).result;
    if (event.oldVersion > 0) {
      this.tryDeleteObjectStore(db, "userdata");
      this.tryDeleteObjectStore(db, "guias");
      this.tryDeleteObjectStore(db, "marcacoes");
      this.tryDeleteObjectStore(db, "comentarios");
      this.tryDeleteObjectStore(db, "referencias");
      this.tryDeleteObjectStore(db, "grifos");
      this.tryDeleteObjectStore(db, "instituicoes");
      this.tryDeleteObjectStore(db, "bancas");
      this.tryDeleteObjectStore(db, "anos");
      this.tryDeleteObjectStore(db, "tipos");
      this.tryDeleteObjectStore(db, "cargos");
      this.tryDeleteObjectStore(db, "referencias-cabecalhos");
    }

    db.createObjectStore("userdata", { keyPath: "id" }).createIndex(
      "id",
      "id",
      { unique: true }
    );
    db.createObjectStore("guias", { keyPath: "id" }).createIndex("id", "id", {
      unique: true,
    });
    db.createObjectStore("marcacoes", { keyPath: "id" }).createIndex(
      "id",
      "id",
      { unique: true }
    );
    db.createObjectStore("comentarios", { keyPath: "id" }).createIndex(
      "id",
      "id",
      { unique: true }
    );
    db.createObjectStore("referencias", { keyPath: "id" }).createIndex(
      "id",
      "id",
      { unique: true }
    );
    db.createObjectStore("grifos", { keyPath: "id" }).createIndex("id", "id", {
      unique: true,
    });
    db.createObjectStore("apontamentos", { keyPath: "id" }).createIndex(
      "id",
      "id",
      { unique: true }
    );
    db.createObjectStore("instituicoes", { keyPath: "id" }).createIndex(
      "id",
      "id",
      { unique: true }
    );
    db.createObjectStore("bancas", { keyPath: "id" }).createIndex("id", "id", {
      unique: true,
    });
    db.createObjectStore("anos", { keyPath: "id" }).createIndex("id", "id", {
      unique: true,
    });
    db.createObjectStore("tipos", { keyPath: "id" }).createIndex("id", "id", {
      unique: true,
    });
    db.createObjectStore("cargos", { keyPath: "id" }).createIndex("id", "id", {
      unique: true,
    });

    db.createObjectStore("referencias-cabecalhos", {
      keyPath: "id",
    }).createIndex("id", "id", {
      unique: true,
    });
  }

  private upgradeBancoSvdmData(event: IDBVersionChangeEvent) {
    const db = (<IDBOpenDBRequest>event.target).result;
    if (event.oldVersion > 0) {
      this.tryDeleteObjectStore(db, "lei-info");
      this.tryDeleteObjectStore(db, "lei-conteudo");
      this.tryDeleteObjectStore(db, "lei-download");
      this.tryDeleteObjectStore(db, "estatisticas");
      this.tryDeleteObjectStore(db, "datas-controle");
      this.tryDeleteObjectStore(db, "error-log");
      this.tryDeleteObjectStore(db, "comentario-gereciado-conteudo");
      this.tryDeleteObjectStore(db, "referencias-gereciada-conteudo");
    }
    db.createObjectStore("lei-info", {
      keyPath: "id",
    }).createIndex("id", "id", {
      unique: true,
    });

    db.createObjectStore("lei-conteudo", {
      keyPath: "id",
    }).createIndex("id", "id", {
      unique: true,
    });

    db.createObjectStore("lei-download", {
      keyPath: "id",
    }).createIndex("id", "id", {
      unique: true,
    });

    db.createObjectStore("estatisticas", {
      keyPath: "id",
    }).createIndex("id", "id", {
      unique: true,
    });

    db.createObjectStore("datas-controle", {
      keyPath: "id",
    }).createIndex("id", "id", {
      unique: true,
    });

    db.createObjectStore("error-log", {
      keyPath: "id",
    }).createIndex("id", "id", {
      unique: true,
    });

    db.createObjectStore("comentario-gereciado-conteudo", {
      keyPath: "idLei",
    }).createIndex("idLei", "idLei", {
      unique: true,
    });

    db.createObjectStore("referencias-gereciada-conteudo", {
      keyPath: "idLei",
    }).createIndex("idLei", "idLei", {
      unique: true,
    });
  }

  private excluirBanco(nomeBanco: string) {
    new Promise<void>((onsuccess, onerror) => {
      const request = indexedDB.deleteDatabase(nomeBanco);
      request.onsuccess = () => {
        onsuccess();
      };
      request.onerror = (error) => {
        this.errorHandlerService.handleError(error);
        onerror();
      };
    });
  }
}
